<script>
   /*************************************************************/
   /* Root component for the application                        */
   /*************************************************************/

   import { date2str } from '@mdatools-apps/shared/utils';
   import { checkPrepField, checkPrepChain } from '@mdatools-apps/shared/methods/prep.js';
   import { replacer, reviver } from './utils.svelte';

   import TrainAppContainer from './tab_train/AppContainer.svelte';
   import TestAppContainer from './tab_test/AppContainer.svelte';
   import NewAppContainer from './tab_new/AppContainer.svelte';
   import App from '@mdatools-apps/shared/components/MainApp.svelte';
   import WASM from '@mdatools-apps/wasm/pca_wasm.js';

   /* fills in keys added after a session was written, so an older one is not thrown away. 'center'
      and 'scale' are written only when absent - an explicit 'false' has to survive, which is why
      this asks whether the key is there and not whether it is truthy */
   function normalizeSession(s) {
      if (!Object.hasOwn(s.train, 'center')) s.train.center = s.train.nimca?.center ?? true;
      if (!Object.hasOwn(s.train, 'scale')) s.train.scale = s.train.nimca?.scale ?? false;

      // alpha, gamma and the limits are taken from a stored model even when the keys are there:
      // the selectors show the model's, and a version without the keys changed only the model.
      // a numeric string becomes a number in the model and here, as in the model loader
      const num = v => typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
      const m = s.train.nimca && typeof s.train.nimca === 'object' ? s.train.nimca : null;
      if (m) for (const k of ['alpha', 'gamma']) m[k] = num(m[k]);
      const take = (k, d) => m ? m[k] : Object.hasOwn(s.train, k) ? s.train[k] : d;
      s.train.alpha = num(take('alpha', 5));
      s.train.gamma = num(take('gamma', 1));
      s.train.limType = take('limType', 'classic');
   }

   /* callback to check validity of JSON with session */
   function checkSession(s) {

      const keys1 = Object.keys(s);
      if (!['date', 'tab', 'train', 'test', 'new'].every(v => keys1.includes(v))) return false;

      const keys2 = Object.keys(s.train);
      if (!['nimca', 'calset', 'calseto', 'calsetp', 'className', 'calres', 'outliers', 'prepType',
         'prepmodel', 'prepmeta', 'prepobj'].every(v => keys2.includes(v))) return false;

      const keys3 = Object.keys(s.test);
      if (!['nimca', 'prep', 'testset', 'testseto', 'testsetp', 'testres', 'outliers'].every(v => keys3.includes(v))) return false;

      const keys4 = Object.keys(s.new);
      if (!['nimca', 'prep', 'newset', 'newseto', 'newsetp', 'newres', 'outliers'].every(v => keys4.includes(v))) return false;

      // a stored preprocessing model is rendered as soon as its tab mounts, and the chain read by
      // the creator, where no catch reaches: the checks a file passes (prep's 'checkSession' runs
      // the same); 0 and null are none
      try {
         checkPrepChain(s.train.prepobj);
         for (const p of [s.train.prepmodel, s.test.prep, s.new.prep]) checkPrepField(p);
      } catch { return false; }

      // the model settings are checked after the keys above rather than with them: they were
      // added later, and requiring them there would throw away every session written before that
      normalizeSession(s);
      if (typeof s.train.center !== 'boolean' || typeof s.train.scale !== 'boolean') return false;
      // the model loader's checks: anything else builds a model the loader refuses, and some
      // values, alpha 100 for one, crash the tab
      const isPerc = v => Number.isFinite(v) && v > 0 && v < 100;
      if (!isPerc(s.train.alpha) || !isPerc(s.train.gamma) ||
         !['classic', 'robust'].includes(s.train.limType)) return false;

      return true;
   }

   /* callback to initialize new session */
   function initSession() {

      return {
         date: date2str(new Date()),
         tab: 0,
         train: {
            nimca: 0,
            calset: 0,
            calseto: 0,
            calsetp: 0,
            className: null,
            calres: 0,
            // the model settings live here and not in 'NIMCASettings' or the model: every change
            // which clears the preprocessed set unmounts the one and drops the other, and the
            // settings went back to the defaults with them
            center: true,
            scale: false,
            alpha: 5,
            gamma: 1,
            limType: 'classic',
            outliers: [],
            prepType: null,
            prepmodel: 0,
            prepmeta: 0,
            prepobj: [],
         },
         test: {
            nimca: 0,
            prep: 0,
            testset: 0,
            testseto: 0,
            testsetp: 0,
            testres: 0,
            outliers: [],
         },
         new: {
            nimca: 0,
            prep: 0,
            newset: 0,
            newseto: 0,
            newsetp: 0,
            newres: 0,
            outliers: []
         }
      }
   }

   // app containers
   const tabs = [
      {name: 'Train', label: 'train', component: TrainAppContainer},
      {name: 'Test', label: 'test', component: TestAppContainer},
      {name: 'Predict', label: 'new', component: NewAppContainer},
   ]
</script>

<App
   appID="nimca"
   appTitle="NIMCA"
   appShortTitle="NIMCA"
   appSlogan="Naïve DD-SIMCA classifier"
   {WASM}
   {tabs}
   {initSession}
   {checkSession}
   {replacer}
   {reviver}
>

   {#snippet footer()}
      <p>Current release: <span>1.5.1</span>
      &nbsp;Feedback: <a class="paper-link" href="mailto:help@mda.tools?subject=NIMCA app">help@mda.tools</a>
      </p>
   {/snippet}

   {#snippet info()}
   <h2>NIMCA</h2>
   <p>Authors: <em>Oxana Rodionova, Alexey Pomerantsev, Sergey Kucheryavskiy</em></p>
   <p>
      Interactive web-application for creating and using Naïve DD-SIMCA (NIMCA) one class classifier
      directly in your browser. The app does not send any information to a server,
      all calculations are done locally on your device.
   </p>
   <p>Check  <a href="https://doi.org/10.1016/j.microc.2025.116662">paper</a> for all details.</p>
   <h3>v. 1.5.1</h3>
   <p class="release-info">released 01/11/2026</p>
   <ul>
      <li>New higher-contrast theme for the control elements.</li>
      <li>Improved accessibility of the controls.</li>
      <li>Added possibility to save plot data as CSV file.</li>
      <li>New more efficient and faster session system.</li>
      <li>Small bug fixes and improvements.</li>
   </ul>
   {/snippet}
</App>

